import PropTypes from 'prop-types';
import React, { useState, useEffect } from 'react';

import { bombasColors } from 'Styles/colors';
import { convertColorToThemeHexCode } from 'Styles/colors.util';
import * as Styled from './ContentCard.styled';
import CtaPropType from '../../prop-types/cta';
import RichTextPropTypes from '../../prop-types/rich-text';
import RichText from '../RichText/RichText';
import Reveal from '../Reveal/Reveal';

/**
 * ContentCard component
 *
 * This component can be displayed in a grid or carousel
 * and will display:
 *
 *  - an image
 *  - a title
 *  - a subheader
 *  - one link or more links that will be displayed as buttons
 *
 */
const ContentCard = ({
  alignment,
  backgroundColor,
  ctas,
  description,
  gridSpan,
  header,
  height,
  width,
  src,
  subheader,
  responsiveStyleSize,
  titleFontSize,
  className,
  containerBackgroundColor
}) => {
  const [areCtasRevealed, setAreCtasRevealed] = useState(false);
  const isTransparent = backgroundColor === 'transparent';
  const backgroundHexcode =
    (!isTransparent && convertColorToThemeHexCode(backgroundColor)) ||
    containerBackgroundColor;

  const handleGlobalClick = () => setAreCtasRevealed(false);

  useEffect(() => {
    if (areCtasRevealed) {
      document.addEventListener('click', handleGlobalClick, {
        capture: true,
        once: true
      });
    }

    return () => {
      document.removeEventListener('click', handleGlobalClick, {
        capture: true
      });
    };
  }, [areCtasRevealed]);

  const cardImage = (
    <Styled.Image
      background={backgroundHexcode}
      crop={{
        height,
        width
      }}
      cropType="fit"
      $isTransparent={isTransparent}
      resolution={{
        small: 270,
        medium: 760
      }}
      src={src.match(/https|http/gi) ? src : `https://${src}`}
      alt={description || ''}
      onClick={() => ctas.length > 1 && setAreCtasRevealed(true)}
      $isClickable={ctas.length > 0}
      $areCtasRevealed={areCtasRevealed}
    />
  );

  return (
    <Styled.ContentCard
      $backgroundColor={backgroundColor}
      $gridSpan={gridSpan}
      $areCtasRevealed={areCtasRevealed}
      className={className}
    >
      {ctas.length === 1 ? (
        <Styled.Link
          href={ctas[0].url}
          aria-label={description || header || 'Bombas'}
        >
          {cardImage}
        </Styled.Link>
      ) : (
        cardImage
      )}
      <Styled.Copy
        $backgroundColor={backgroundHexcode}
        $alignment={alignment}
        $gridSpan={gridSpan}
        $withPadding={!isTransparent}
        $responsiveStyleSize={responsiveStyleSize}
        $areCtasRevealed={areCtasRevealed}
      >
        {!!header && (
          <Styled.Title
            $backgroundColor={backgroundHexcode}
            $responsiveStyleSize={responsiveStyleSize}
            $titleFontSize={titleFontSize}
          >
            {ctas.length === 1 ? (
              <Styled.Link href={ctas[0].url}>{header}</Styled.Link>
            ) : (
              header
            )}
          </Styled.Title>
        )}
        {!!subheader && !!Object.keys(subheader).length && (
          <Styled.Subheader $responsiveStyleSize={responsiveStyleSize}>
            <RichText richText={subheader} />
          </Styled.Subheader>
        )}
        {ctas.length === 1 && !!(ctas[0].text && ctas[0].text.trim()) && (
          <Styled.Cta
            as="a"
            href={ctas[0].url}
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
            $backgroundColor={backgroundHexcode}
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
        {ctas.length > 1 && ctas[0].text && ctas[0].text.trim() && (
          <Styled.Cta
            $backgroundColor={backgroundHexcode}
            key={header + ctas[0].text + ctas[0].url}
            onClick={() => setAreCtasRevealed(true)}
            role="button"
            size={responsiveStyleSize === 'mega' ? 'medium' : 'small'}
            variant="defaultDark"
          >
            {ctas[0].text}
          </Styled.Cta>
        )}
      </Styled.Copy>
      {ctas.length > 1 && areCtasRevealed && (
        <Reveal
          ctas={ctas}
          setIsOpen={setAreCtasRevealed}
          size={responsiveStyleSize}
        />
      )}
    </Styled.ContentCard>
  );
};

ContentCard.propTypes = {
  /**
   * Alignment of text
   */
  alignment: PropTypes.oneOf(['left', 'center']),
  /**
   * Background color
   */
  backgroundColor: PropTypes.oneOf(['transparent', ...bombasColors]),
  /**
   * Links to display
   */
  ctas: PropTypes.arrayOf(PropTypes.shape(CtaPropType)),
  /**
   * img tag alt text
   */
  description: PropTypes.string,
  /**
   * Column spans in a 12 column grid
   *
   * Accepts a number or a string
   */
  gridSpan: ({ gridSpan }, propName, componentName) =>
    gridSpan && gridSpan >= 1 && gridSpan <= 12
      ? null
      : new Error(`${propName} in ${componentName} is not between 1 and 12`),
  /**
   * Image height
   */
  height: PropTypes.number,
  /**
   * Title to display
   */
  header: PropTypes.string,
  /**
   * Image src to render
   */
  src: PropTypes.string,
  /**
   * The supporting text for the header.
   *
   * Renders as contentful rich text
   */
  subheader: PropTypes.shape(RichTextPropTypes),
  /**
   * Image width
   */
  width: PropTypes.number,
  /**
   * Applies styling to copy based on content system responsive rules if titleFontSize is not passed in
   */
  responsiveStyleSize: PropTypes.oneOf(['mini', 'regular', 'mega']),
  /**
   * Changes the size of the header text underneath the Content Card
   */
  titleFontSize: PropTypes.number,
  /**
   * optional styled overrides
   */
  className: PropTypes.string,
  /**
   * Background color of the parent container
   */
  containerBackgroundColor: PropTypes.string
};

ContentCard.defaultProps = {
  alignment: 'left',
  backgroundColor: 'white',
  ctas: [],
  description: '',
  gridSpan: 6,
  header: '',
  height: 400,
  src: '',
  subheader: {},
  width: 400,
  responsiveStyleSize: 'regular',
  titleFontSize: undefined,
  className: undefined,
  containerBackgroundColor: convertColorToThemeHexCode('white')
};

export default ContentCard;
